import { Component, computed, effect, inject, input, resource, signal } from '@angular/core';
import { TranslatePipe } from '@cmi/addin-core';
import { ContentProviderProperty, Field, RootType } from '../home/home.models';
import { HomeService } from '../home/home.service';
import { ContentControlEditor } from './content-control-editor';
import { PropertyFormatForm } from './property-format-form';

@Component({
  selector: 'cmi-property-format',
  imports: [PropertyFormatForm, TranslatePipe, ContentControlEditor],
  templateUrl: './property-format.html'
})
export class PropertyFormat {
  private readonly _homeService = inject(HomeService);

  private readonly _xpathField = signal<ContentProviderProperty | null>(null);

  public readonly contentControlId = input.required<number>();

  constructor() {
    // When ID changes, fetch the control info
    effect(() => {
      const id = this.contentControlId();
      if (id) {
        void this._fetchContentControlInfo();
      }
    });

    // Watch for successful format application and trigger refresh
    effect(() => {
      const error = this._applyFormatResource.error();
      const isLoading = this._applyFormatResource.isLoading();
      const wasLoading = this._previousApplyLoading();

      // Trigger refresh when transitioning from loading to completed successfully
      if (wasLoading && !isLoading && !error) {
        // Increment trigger to cause ContentControlEditor to refetch
        this.refreshTriggerSignal.update((v) => v + 1);
      }

      // Update previous loading state
      this._previousApplyLoading.set(isLoading);
    });
  }

  public readonly titleSignal = signal<string>('');
  public readonly tagSignal = signal<string>('');
  public readonly isExpanded = signal<boolean>(false);
  public readonly refreshTriggerSignal = signal<number>(0);
  private readonly _previousApplyLoading = signal<boolean>(false);

  public readonly pageTitle = computed(() => {
    const title = this.titleSignal();
    const match = title.match(/^CMI_[^.]+\.(.+?)(?::.*)?$/);
    if (match) {
      return match[1];
    }
    // For non-CMI_ controls (e.g. ITEM), use the title directly (strip format suffix after colon)
    const baseTitle = title.includes(':') ? title.split(':')[0] : title;
    if (baseTitle.startsWith('ITEM.')) {
      return baseTitle.substring('ITEM.'.length);
    }
    // Provider-level controls have no .property part: CMI_Betreff -> Betreff
    return baseTitle.startsWith('CMI_') ? baseTitle.substring('CMI_'.length) : baseTitle;
  });

  // Extract format suffix from title (part after colon)
  public readonly formatSuffix = computed(() => {
    const title = this.titleSignal();
    if (!title || !title.includes(':')) {
      return null;
    }
    const parts = title.split(':');
    return parts.length > 1 ? parts.slice(1).join(':') : null;
  });

  public readonly sourceProperty = computed(() => {
    const title = this.titleSignal();
    if (!title) {
      return null;
    }

    // Extract base title (before colon if format suffix exists)
    const baseTitle = title.includes(':') ? title.split(':')[0] : title;

    // Check if it's a CMI_XPath control
    if (baseTitle.startsWith('CMI_XPathObj.')) {
      return this._xpathField();
    }

    // ITEM controls inside table contexts only carry the property key in the title.
    // Resolve the property by unique key across content providers.
    if (baseTitle.startsWith('ITEM.')) {
      const propertyKey = baseTitle.substring('ITEM.'.length);
      const rootType = this._homeService.rootType();
      if (!rootType || !propertyKey) {
        return null;
      }

      return this._findPropertyByUniqueKey(rootType, propertyKey);
    }

    // Handle traditional CMI_Provider.Property format
    // Remove CMI_ prefix if present
    const titleWithoutPrefix = baseTitle.startsWith('CMI_') ? baseTitle.substring(4) : baseTitle;

    // Split by . to get sectionkey and propertykey
    const parts = titleWithoutPrefix.split('.');
    if (parts.length !== 2) {
      return null;
    }

    const [sectionkey, propertykey] = parts;

    // Query RootType to find the matching ContentProvider and Property
    const rootType = this._homeService.rootType();
    if (!rootType) {
      return null;
    }

    const provider = (rootType.contentProvider ?? []).find((p) => p.key === sectionkey);
    if (!provider) {
      return null;
    }

    const property = (provider.properties ?? []).find((p) => p.key === propertykey);
    return property ?? null;
  });

  private _findPropertyByUniqueKey(
    rootType: RootType,
    propertyKey: string
  ): ContentProviderProperty | null {
    const matches = (rootType.contentProvider ?? []).flatMap((provider) =>
      (provider.properties ?? []).filter((property) => property.key === propertyKey)
    );

    if (matches.length !== 1) {
      return null;
    }

    return matches[0];
  }

  public readonly hasFormatOptions = computed(() => {
    const prop = this.sourceProperty();
    if (!prop) {
      return false;
    }

    const typeName = this._normalizeTypeName(prop.typeName);
    return (
      typeName === 'datetime' ||
      typeName === 'date' ||
      typeName === 'boolean' ||
      typeName === 'bool' ||
      typeName === 'int32' ||
      typeName === 'numeric'
    );
  });

  private _normalizeTypeName(typeName: string | null | undefined): string {
    if (!typeName) {
      return '';
    }

    let normalized = typeName.trim();

    const nullableGenericMatch = normalized.match(/^Nullable<(.+)>$/i);
    if (nullableGenericMatch) {
      normalized = nullableGenericMatch[1].trim();
    }

    const nullableClrMatch = normalized.match(/^Nullable`1\[(.+)]$/i);
    if (nullableClrMatch) {
      normalized = nullableClrMatch[1].trim();
    }

    normalized = normalized.replace(/^System\./i, '').replace(/^Edm\./i, '');

    return normalized.toLowerCase();
  }

  private async _fetchContentControlInfo(): Promise<void> {
    const id = this.contentControlId();
    if (!id) {
      this._xpathField.set(null);
      return;
    }

    try {
      await Word.run(async (context) => {
        const contentControls = context.document.contentControls;
        contentControls.load('items');
        await context.sync();

        const control = contentControls.items.find((c) => {
          c.load('id');
          return true;
        });

        if (!control) {
          this._xpathField.set(null);
          return;
        }

        await context.sync();
        const foundControl = contentControls.items.find((c) => c.id === id);

        if (!foundControl) {
          this._xpathField.set(null);
          return;
        }

        foundControl.load(['title', 'tag']);
        await context.sync();

        // Store title and tag in signals
        this.titleSignal.set(foundControl.title);
        this.tagSignal.set(foundControl.tag);

        if (!foundControl.title.startsWith('CMI_XPathObj.')) {
          this._xpathField.set(null);
          return;
        }

        // Extract field key from title: CMI_XPathObj.fieldKey or CMI_XPathObj.fieldKey:format
        const titleWithoutPrefix = foundControl.title.substring('CMI_XPathObj.'.length);
        const fieldKey = titleWithoutPrefix.includes(':')
          ? titleWithoutPrefix.split(':')[0]
          : titleWithoutPrefix;

        // Parse tag: objpath:Dokument/path/to/assoc (does not include field key)
        const tag = foundControl.tag;
        if (!tag || !tag.startsWith('objpath:')) {
          this._xpathField.set(null);
          return;
        }

        const fullPath = tag.substring('objpath:'.length);
        const assocPath = fullPath.startsWith('Dokument/')
          ? fullPath.substring('Dokument/'.length)
          : fullPath;

        // Combine assoc path with field key
        const fieldPath = assocPath ? `${assocPath}/${fieldKey}` : fieldKey;

        // Navigate through the model to find the field
        const field = await this._findFieldByPath(fieldPath);
        if (field) {
          this._xpathField.set({
            name: field.name,
            key: field.key,
            typeName: field.dataType,
            formatString: undefined
          });
        } else {
          this._xpathField.set(null);
        }
      });
    } catch (error) {
      console.error('Error fetching XPath field info:', error);
      this._xpathField.set(null);
    }
  }

  private async _findFieldByPath(path: string): Promise<Field | null> {
    const segments = path.split('/');
    const fieldKey = segments[segments.length - 1];
    const assocSegments = segments.slice(0, -1);

    let currentData: RootType | null = this._homeService.rootType() ?? null;
    if (!currentData) {
      return null;
    }

    // Navigate through associations
    for (const segmentKey of assocSegments) {
      const assocs = currentData.assocs ?? [];
      const assoc = assocs.find((a) => a.key === segmentKey);

      if (!assoc || assoc.allowedTypes.length === 0) {
        return null;
      }

      // Fetch the allowed type data
      const firstAllowedType = assoc.allowedTypes[0];
      try {
        currentData = await new Promise<RootType>((resolve, reject) => {
          this._homeService.getAllowedTypes(firstAllowedType.key).subscribe({
            next: (data) => resolve(data),
            error: (err) => reject(err instanceof Error ? err : new Error(String(err)))
          });
        });
      } catch (error) {
        console.error('Error fetching allowed type:', error);
        return null;
      }
    }

    // Find the field at the current level
    const fields = currentData.fields ?? [];
    return fields.find((f) => f.key === fieldKey) ?? null;
  }

  public readonly rootTypeLoading = computed(() => this._homeService.rootTypeLoading());
  public readonly rootTypeError = computed(() => this._homeService.rootTypeError());
  public readonly rootTypeErrorString = computed(() => {
    const error = this.rootTypeError();
    if (!error) {
      return '';
    }
    return error instanceof Error ? error.message : String(error);
  });

  private readonly _applyFormatParams = signal<{
    id: number;
    title: string;
    formatValue: string;
  } | null>(null);

  private readonly _applyFormatResource = resource<
    void,
    { id: number; title: string; formatValue: string }
  >({
    params: () => {
      const params = this._applyFormatParams();
      if (!params) {
        return { id: 0, title: '', formatValue: '' };
      }
      return params;
    },
    loader: async ({ params }) => {
      if (!params.id || !params.title || !params.formatValue) {
        return;
      }

      const baseTitle = params.title.includes(':') ? params.title.split(':')[0] : params.title;
      const newTitle = `${baseTitle}:${params.formatValue}`;

      await Word.run(async (context) => {
        const contentControls = context.document.contentControls;
        contentControls.load('items');
        await context.sync();

        // Load IDs for all controls
        for (const control of contentControls.items) {
          control.load('id');
        }
        await context.sync();

        // Find the control by ID
        const foundControl = contentControls.items.find((c) => c.id === params.id);
        if (foundControl) {
          foundControl.title = newTitle;
          await context.sync();
        }
      });
    }
  });

  public readonly isApplying = computed(() => this._applyFormatResource.isLoading());
  public readonly applyError = computed(() => this._applyFormatResource.error());

  public onApplyFormat(event: { formatValue: string }): void {
    const id = this.contentControlId();
    const title = this.titleSignal();
    if (!id || !title) {
      return;
    }

    this._applyFormatParams.set({ id, title, formatValue: event.formatValue });
    this._applyFormatResource.reload();
  }

  public toggleExpanded(): void {
    this.isExpanded.update((value) => !value);
  }
}
